import React, { useState } from 'react';
import { Shield, Award, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { MobileCarousel } from '../components/MobileCarousel';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { countryCodes } from '../mockData';

export const LandingPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', countryCode: '+1', contactNumber: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('LP Form submitted:', formData);
    alert('Thank you! Your request has been submitted. We will contact you soon.');
    setFormData({ name: '', email: '', countryCode: '+1', contactNumber: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="relative bg-[#58225a] py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1758599543152-a73184816eba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhhbmRzaGFrZSUyMGRlYWx8ZW58MHx8fHwxNzc2Mjg0ODk2fDA&ixlib=rb-4.1.0&q=85" alt="Two businessmen shaking hands outside" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#58225a]/85"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">Lock In Your Brand Rights Today</h1>
              <p className="text-xl md:text-2xl mb-8 text-gray-300">Expert trademark registration services starting at just $45. Protect your business identity with attorneys who know the USPTO inside and out.</p>
              <div className="space-y-4 mb-8">
                {["Federal protection covering all fifty states", "In-depth conflict search before every filing", "A dedicated case manager on every application", "98% approval rate with rapid turnaround"].map((t, i) => (
                  <div key={i} className="flex items-start space-x-3"><CheckCircle2 className="h-6 w-6 text-[#e8a8c8] flex-shrink-0 mt-1" /><span className="text-lg">{t}</span></div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-6 py-8 border-t border-b border-gray-600">
                {[{ v: "10K+", l: "Trademarks Filed" }, { v: "98%", l: "Approval Rate" }, { v: "15+", l: "Years Exp." }].map((s, i) => (
                  <div key={i} className="text-center"><div className="text-4xl font-bold mb-2">{s.v}</div><div className="text-gray-400">{s.l}</div></div>
                ))}
              </div>
            </div>
            <div>
              <Card className="p-8 shadow-2xl bg-white">
                <h2 className="text-3xl font-bold text-[#58225a] mb-2">Request a Consultation</h2>
                <p className="text-gray-600 mb-6">Share your details and a specialist will reach out within 24 hours</p>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div><Label htmlFor="lp-name">Name *</Label><Input id="lp-name" type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-1" placeholder="Your full name" /></div>
                  <div><Label htmlFor="lp-email">Email *</Label><Input id="lp-email" type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="mt-1" placeholder="Your email address" /></div>
                  <div>
                    <Label htmlFor="lp-contact">Contact Number *</Label>
                    <div className="flex gap-2 mt-1">
                      <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value })}><SelectTrigger className="w-32"><SelectValue /></SelectTrigger><SelectContent>{countryCodes.map((item) => (<SelectItem key={item.code} value={item.code}>{item.code}</SelectItem>))}</SelectContent></Select>
                      <Input id="lp-contact" type="tel" required value={formData.contactNumber} onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); if (v.length <= 10) setFormData({ ...formData, contactNumber: v }); }} placeholder="Phone number" className="flex-1" maxLength={10} />
                    </div>
                  </div>
                  <div><Label htmlFor="lp-message">Message</Label><Textarea id="lp-message" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="mt-1" placeholder="Describe your trademark needs..." rows={4} /></div>
                  <Button type="submit" className="w-full bg-[#ba2a84] hover:bg-[#8a1f63] py-6 text-lg font-semibold">Submit Request</Button>
                  <p className="text-xs text-gray-500 text-center">By submitting this form, you agree to our Privacy Policy and Terms of Service</p>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#58225a] mb-4">Why Work With Trademark Examining Lawyer?</h2>
            <p className="text-lg text-gray-600">Reliable trademark solutions backed by proven results</p>
          </div>
          <div className="hidden md:grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { icon: Shield, t: "Attorney-Led Filings", d: "Every application is reviewed and filed by a licensed trademark attorney with years of USPTO experience" },
              { icon: Clock, t: "Fast Turnaround", d: "Efficient workflows and digital filing systems mean your application is submitted promptly and tracked in real time" },
              { icon: Award, t: "Consistent Results", d: "A 98% approval rate built on rigorous searches, precise drafting, and responsive office-action handling" }
            ].map((c, i) => (
              <Card key={i} className="p-8 text-center hover:shadow-xl transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary text-white mb-6"><c.icon className="h-8 w-8" /></div>
                <h3 className="text-2xl font-bold text-[#58225a] mb-4">{c.t}</h3>
                <p className="text-gray-600 leading-relaxed">{c.d}</p>
              </Card>
            ))}
          </div>
          <MobileCarousel>
            {[
              { icon: Shield, t: "Attorney-Led Filings", d: "Every application is reviewed and filed by a licensed trademark attorney with years of USPTO experience" },
              { icon: Clock, t: "Fast Turnaround", d: "Efficient workflows and digital filing systems mean your application is submitted promptly and tracked in real time" },
              { icon: Award, t: "Consistent Results", d: "A 98% approval rate built on rigorous searches, precise drafting, and responsive office-action handling" }
            ].map((c, i) => (
              <Card key={i} className="p-8 text-center shadow-lg">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary text-white mb-6"><c.icon className="h-8 w-8" /></div>
                <h3 className="text-2xl font-bold text-[#58225a] mb-4">{c.t}</h3>
                <p className="text-gray-600 leading-relaxed">{c.d}</p>
              </Card>
            ))}
          </MobileCarousel>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#58225a] mb-4">Plans for Every Budget</h2>
            <p className="text-lg text-gray-600">Starting at just $45 + Federal Filing Fee</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              { p: "$45", n: "Essentials", d: "Core filing with basic search" },
              { p: "$149", n: "Enhanced", d: "Direct-hit search & alerts" },
              { p: "$299", n: "Deluxe", d: "Full protection & case manager", pop: true },
              { p: "$349", n: "Platinum", d: "Premium priority service" }
            ].map((plan, i) => (
              <Card key={i} className={`p-6 text-center hover:shadow-lg transition-shadow ${plan.pop ? 'border-2 border-[#e8a8c8]' : ''}`}>
                {plan.pop && <div className="text-xs bg-[#ba2a84] text-white px-3 py-1 rounded-full inline-block mb-2">POPULAR</div>}
                <div className="text-3xl font-bold text-[#e8a8c8] mb-2">{plan.p}</div>
                <div className="text-gray-600 mb-4">{plan.n}</div>
                <p className="text-sm text-gray-600">{plan.d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-20 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1767342976865-d2aa3aa14bc3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGNpdHlzY2FwZSUyMHNreWxpbmUlMjBuaWdodCUyMGRhcmt8ZW58MHx8fHwxNzc2Mjg2OTQzfDA&ixlib=rb-4.1.0&q=85" alt="City skyline illuminated at night" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#58225a]/80"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6">Every Day Without Protection Is a Risk</h2>
          <p className="text-xl mb-8 text-gray-300 max-w-2xl mx-auto">Secure your brand identity today with Trademark Examining Lawyer. Our attorneys are ready to guide you.</p>
          <Button size="lg" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="bg-[#ba2a84] hover:bg-[#8a1f63] text-white text-lg px-8 py-6 font-semibold">
            Get Started Now
          </Button>
        </div>
      </section>
    </div>
  );
};
